import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import type { ToolConfigScope } from '../model/useAgentConfig/useAgentConfig';

interface ToolAgentKey {
  toolId: string;
  agentId: string;
}

/**
 * Returns workspace objects where every tool+agent pair has coverage
 * (either org-level config or a workspace-specific config for that workspace).
 */
export function getFullyConfiguredWorkspaces(
  pairs: ToolAgentKey[],
  scopes: Map<string, ToolConfigScope>,
  allWorkspaces: WorkspaceResponseDto[],
): WorkspaceResponseDto[] {
  if (pairs.length === 0) return [];

  const allWsIds = new Set<string>();
  const pairScopes = pairs.map((pair) => {
    const scope = scopes.get(`${pair.toolId}:${pair.agentId}`);
    const wsIds = scope?.workspaceIds ?? [];
    wsIds.forEach((id) => allWsIds.add(id));
    return { hasOrg: scope?.hasOrg ?? false, wsIds: new Set(wsIds) };
  });

  return allWorkspaces.filter(
    (ws) => allWsIds.has(ws.id) && pairScopes.every((s) => s.hasOrg || s.wsIds.has(ws.id)),
  );
}

/**
 * Returns workspace IDs where every tool+agent pair has coverage.
 * Use when workspace objects aren't needed (e.g. for ID-only comparisons).
 */
export function getFullyConfiguredWorkspaceIds(
  pairs: ToolAgentKey[],
  scopes: Map<string, ToolConfigScope>,
): string[] {
  if (pairs.length === 0) return [];

  const allWsIds = new Set<string>();
  const pairScopes = pairs.map((pair) => {
    const scope = scopes.get(`${pair.toolId}:${pair.agentId}`);
    const wsIds = scope?.workspaceIds ?? [];
    wsIds.forEach((id) => allWsIds.add(id));
    return { hasOrg: scope?.hasOrg ?? false, wsIds: new Set(wsIds) };
  });

  return Array.from(allWsIds).filter((wsId) =>
    pairScopes.every((s) => s.hasOrg || s.wsIds.has(wsId)),
  );
}
